import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Ban, RotateCcw, CalendarPlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, ErrorBox } from "@/components/Widgets";
import { CaptionButton } from "@/components/CaptionDialog";
import { API, useApi, df, errMsg } from "@/lib/api";
import { iso } from "@/components/calendar/CalendarGrid";

const TABS = [["all", "Tutte"], ["favorite", "Preferite"], ["unused", "Da usare"], ["used", "Già in calendario"], ["discarded", "Scartate"]];
const FORMATS = ["Tutti", "Reel", "Carosello", "Post", "Story", "Live"];
const PLATS = ["Tutte", "Instagram", "Facebook", "Entrambe"];

const Schedule = ({ idea, onDone }) => {
  const [d, setD] = useState(iso(new Date()));
  const [t, setT] = useState("18:30");
  const [open, setOpen] = useState(false);
  const go = async () => {
    try {
      await axios.post(`${API}/ideas/${idea.id}/schedule`, { date: d, time: t });
      toast.success(`Pianificata il ${new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}`);
      setOpen(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`schedule-idea-${idea.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#2A4B3C] px-3 py-1.5 rounded-full hover:bg-[#36604C] transition-colors"><CalendarPlus className="w-3.5 h-3.5" />Metti in calendario</button>
      </PopoverTrigger>
      <PopoverContent className="w-64 rounded-none space-y-3" data-testid={`schedule-popover-${idea.id}`}>
        <Input data-testid="schedule-date" type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" />
        <Input data-testid="schedule-time" type="time" value={t} onChange={(e) => setT(e.target.value)} className="rounded-none" />
        <Button data-testid="schedule-confirm" onClick={go} disabled={!d} className="w-full rounded-full bg-[#2A4B3C] hover:bg-[#36604C]">Conferma</Button>
      </PopoverContent>
    </Popover>
  );
};

const IdeaCard = ({ doc, onPatch, reload }) => {
  const i = doc.idea;
  return (
    <div data-testid={`idea-card-${doc.id}`} className={`bg-white border p-5 flex flex-col transition-colors ${doc.favorite ? "border-[#C9A227]" : "border-[#E8E8E4]"} ${doc.discarded ? "opacity-55" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
          <span className="border border-[#2A4B3C] text-[#2A4B3C] px-1.5 py-0.5">{i.formato}</span>
          <span className="border border-[#C05C3D] text-[#C05C3D] px-1.5 py-0.5">{i.piattaforma}</span>
          {i.obiettivo && <span className="text-[#5C5C58] self-center">{i.obiettivo}</span>}
        </div>
        <div className="flex gap-1 shrink-0">
          <button data-testid={`fav-idea-${doc.id}`} onClick={() => onPatch(doc, { favorite: !doc.favorite })} title="Preferita"><Star className={`w-4 h-4 ${doc.favorite ? "fill-[#C9A227] text-[#C9A227]" : "text-[#BDBDB5] hover:text-[#C9A227]"}`} /></button>
          <button data-testid={`discard-idea-${doc.id}`} onClick={() => onPatch(doc, { discarded: !doc.discarded })} title={doc.discarded ? "Ripristina" : "Scarta"}>
            {doc.discarded ? <RotateCcw className="w-4 h-4 text-[#2A4B3C]" /> : <Ban className="w-4 h-4 text-[#BDBDB5] hover:text-[#C05C3D]" />}
          </button>
        </div>
      </div>
      <h4 className={`font-display text-base font-semibold mt-3 leading-snug ${doc.discarded ? "line-through" : ""}`}>{i.titolo}</h4>
      {i.hook && <p className="text-sm mt-2 border-l-2 border-[#C05C3D] pl-3 italic">"{i.hook}"</p>}
      {i.descrizione && <p className="text-sm text-[#5C5C58] mt-2">{i.descrizione}</p>}
      {i.cta && <p className="text-xs mt-2"><span className="label !text-[9px] mr-2">CTA</span>{i.cta}</p>}
      <div className="text-[11px] text-[#5C5C58] mt-3">
        {doc.source === "strategia" ? "Dalla Strategia AI" : "Creata per il calendario"} · {df(doc.created_at)}
        {doc.uses.length > 0 && <span data-testid={`idea-uses-${doc.id}`} className="block mt-1 text-[#2A4B3C]">In calendario: {doc.uses.map((u) => `${df(u.date)}${u.status === "pubblicato" ? " ✓" : ""}`).join(", ")}</span>}
      </div>
      <div className="mt-auto pt-4 flex flex-wrap gap-2 items-center">
        {!doc.discarded && <Schedule idea={doc} onDone={reload} />}
        <CaptionButton idea={i} index={`idea-${doc.id}`} />
      </div>
    </div>
  );
};

export default function Ideas() {
  const { data, error, loading, reload } = useApi("/ideas");
  const [tab, setTab] = useState("unused");
  const [fmt, setFmt] = useState("Tutti");
  const [plat, setPlat] = useState("Tutte");
  const [q, setQ] = useState("");
  const [local, setLocal] = useState({});
  const docs = useMemo(() => (data || []).map((d) => ({ ...d, ...(local[d.id] || {}) })), [data, local]);
  const counts = useMemo(() => ({
    all: docs.filter((d) => !d.discarded).length, favorite: docs.filter((d) => d.favorite && !d.discarded).length,
    unused: docs.filter((d) => !d.uses.length && !d.discarded).length, used: docs.filter((d) => d.uses.length).length,
    discarded: docs.filter((d) => d.discarded).length,
  }), [docs]);
  const list = docs.filter((d) => {
    if (tab === "discarded" ? !d.discarded : d.discarded) return false;
    if (tab === "favorite" && !d.favorite) return false;
    if (tab === "unused" && d.uses.length) return false;
    if (tab === "used" && !d.uses.length) return false;
    if (fmt !== "Tutti" && !(d.idea.formato || "").toLowerCase().includes(fmt.toLowerCase().slice(0, 5))) return false;
    if (plat !== "Tutte" && !(d.idea.piattaforma || "").toLowerCase().includes(plat.toLowerCase().slice(0, 6))) return false;
    return !q || JSON.stringify(d.idea).toLowerCase().includes(q.toLowerCase());
  }).sort((a, b) => b.favorite - a.favorite);
  const patch = async (doc, body) => {
    setLocal((l) => ({ ...l, [doc.id]: { ...(l[doc.id] || {}), ...body } }));
    try { await axios.patch(`${API}/ideas/${doc.id}`, body); } catch (e) { toast.error(errMsg(e)); setLocal((l) => ({ ...l, [doc.id]: {} })); }
  };
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  return (
    <>
      <PageHeader eyebrow={`${docs.length} idee generate dall'AI`} title="Archivio idee" desc="Tutte le idee della Strategia AI e quelle create per il calendario. Le preferite vengono usate per prime quando riempi il calendario, le scartate non vengono mai usate." />
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="flex border border-[#E8E8E4] bg-white">{TABS.map(([k, l]) => <button key={k} data-testid={`ideas-tab-${k}`} onClick={() => setTab(k)} className={`px-3 py-2 text-xs transition-colors ${tab === k ? "bg-[#2A4B3C] text-white" : "hover:bg-[#F7F7F5]"}`}>{l} <span className="font-mono-num opacity-70">{counts[k]}</span></button>)}</div>
        <select data-testid="ideas-format-filter" value={fmt} onChange={(e) => setFmt(e.target.value)} className="border border-[#E8E8E4] text-xs px-2 py-2 bg-white">{FORMATS.map((f) => <option key={f}>{f}</option>)}</select>
        <select data-testid="ideas-platform-filter" value={plat} onChange={(e) => setPlat(e.target.value)} className="border border-[#E8E8E4] text-xs px-2 py-2 bg-white">{PLATS.map((f) => <option key={f}>{f}</option>)}</select>
        <Input data-testid="ideas-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="rounded-none h-9 w-56" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ideas-grid">
        {list.map((d) => <IdeaCard key={d.id} doc={d} onPatch={patch} reload={() => { setLocal({}); reload(); }} />)}
      </div>
      {!list.length && <p className="text-sm text-[#5C5C58]" data-testid="ideas-empty">Nessuna idea in questa vista.</p>}
    </>
  );
}
